import React, { useCallback, useEffect } from 'react';

import { Flex, useTheme } from '@chakra-ui/react';
import { generateCSVContinousFeedbackDataApi } from 'api/analytics/feedback';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import AddFeedbackButton from 'screens/Levelup/LevelUpCF/components/addFeedbackButton/AddFeedbackButton';
import { FilterBarLoader } from 'screens/analytics/commonComponents';
import DownloadCSVDataV1 from 'screens/analytics/commonComponents/DownloadCSVDataV1';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import { StyledBox } from 'styles/common';

import { ExclamationMark, Feedback } from 'assets/assetsV1/iconComponents';

import { FEEDBACK_FORM_TYPE } from 'constants/ProjectConstants';
import { getFiltersFromLS, pushFeedbackParams } from 'constants/helper';
import { localStorageKeys } from 'constants/localStorageKeys';
import { sendDownloadCSVEvent, sendSelectedGroupByEvent } from 'constants/mixpanelHelper';

import { getCohortKeyValues as getCohortKeyValuesReview } from 'services/analytics/review';

import { Select } from 'uiToolkitV1/Select';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useLevelUpAnalytics } from '../context';
import { useFetchCFAnalyticsFilters } from '../hooks/dataHooks';

const ExclamationMarkIcon = ({ ...props }) => <ExclamationMark {...props} fill="transparent" />;
const FeedbackIcon = ({ ...props }) => <Feedback {...props} fill="transparent" />;

const cohortIdsToSendMixpanelEventsFor = [-1, -2, -3];

const ContinuousFeedbackAnalyticsFilterBar = ({ isDisabled = false }) => {
  const {
    state: {
      allCohorts,
      allGroupByKeys,
      allSplitByKeys,
      currentState,
      reactSelectGroupByForMyTeam,
      reactSelectGroupByForMyOrgAndAllEmps,
    },
    selectGroupBy,
    selectSplitByKey,
    initializeState,
    addCohort,
    setOperationInProgress,
  } = useLevelUpAnalytics();

  const { selectedGroupByKey, selectedSplitByKey, selectedSource, selectedFilter, selectedDateRange } = currentState;

  // const [isLoading, setLoading] = useState(true);

  const theme = useTheme();

  const getCohortKeys = () => getCohortKeyValuesReview('CORE_COMPETENCY');

  const sideNavSelectedCohort = useSelectedView();

  const modifiedGroupByKeys =
    sideNavSelectedCohort?.id === -2 ? reactSelectGroupByForMyTeam : reactSelectGroupByForMyOrgAndAllEmps;

  const generateCSVData = useCallback(() => {
    sendDownloadCSVEvent({
      eventProperties: {
        cohortId: sideNavSelectedCohort?.id,
        cohortName: sideNavSelectedCohort?.name,
        source: 'Feedback',
      },
    });
    return generateCSVContinousFeedbackDataApi({
      cohort: sideNavSelectedCohort,
      groupByKeys: selectedGroupByKey,
      source: selectedSource,
      filter: selectedFilter,
      dateRange: selectedDateRange,
    });
  }, [sideNavSelectedCohort, selectedGroupByKey, selectedSource, selectedFilter, selectedDateRange]);

  const { data: filterData, isLoading, isError } = useFetchCFAnalyticsFilters();
  useEffect(() => {
    if (!isLoading && !isError) {
      const dataInLS = getFiltersFromLS(filterData, localStorageKeys.analyticsLevelUpFilters) ?? {};
      initializeState({
        ...filterData,
        // selectedCohort: dataInLS.cohortFromLS ?? null, //<- uncomment this when BE support is there for grouped by employee for all cohorts
        selectedCohort: null,
        selectedGroupByKey:
          (dataInLS.groupByFromLS ?? sideNavSelectedCohort?.id === -2)
            ? {
                uuid: null,
                keyName: 'employeeId',
                displayName: 'Individual',
                enabled: true,
              }
            : { uuid: null, keyName: 'orgTree', displayName: 'Organisation Tree', enabled: true },
        selectedDateRange: dataInLS.dateRangeFromLS ?? null,
      });
    }
  }, [filterData, isLoading, isError, sideNavSelectedCohort?.id]);

  const history = useHistory();
  const employeeId = useSelector(selectLoggedInEmployeeId);

  const handleRequestNewFeedbackClick = () => {
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.REQUESTED,
      newForm: true,
      empId: employeeId ?? null,
      source: cohortIdsToSendMixpanelEventsFor.includes(sideNavSelectedCohort?.id) ? sideNavSelectedCohort?.name : null,
    });
  };
  const handleShareNewFeedbackClick = (isSharePublicPraise = false) => {
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.PROVIDED,
      newForm: true,
      empId: null,
      source: cohortIdsToSendMixpanelEventsFor.includes(sideNavSelectedCohort?.id) ? sideNavSelectedCohort?.name : null,
      isSharePublicPraise,
    });
  };

  // const allEmployeesCohort = allCohorts?.find(({ id }) => id === -1);

  // const orgTreeCohorts = allCohorts?.filter(({ id }) => OrgTreeVisibleCohortsIdArray.includes(id));

  // const modifiedGroupByKeys = ['-1', '-3'].includes(sideNavSelectedCohort?.uuid)
  //   ? allGroupByKeys?.filter(({ value }) => value !== 'employeeId')
  //   : allGroupByKeys?.filter(({ value }) => value !== 'orgTree');

  const selectedGroupBy = modifiedGroupByKeys?.find(({ value }) => value === selectedGroupByKey?.keyName);

  const selectedSplitBy = allSplitByKeys?.find(({ value }) => value === selectedSplitByKey?.keyName);

  const splitButtonOptions = [
    {
      value: 'shareFeedback',
      label: 'Share Feedback',
      icon: ExclamationMarkIcon,
      onClick: handleShareNewFeedbackClick,
    },
    {
      value: 'requestFeedback',
      label: 'Request feedback',
      icon: FeedbackIcon,
      onClick: handleRequestNewFeedbackClick,
    },
  ];

  return (
    <Flex sx={{ marginBottom: '24px' }}>
      {isLoading ? (
        <FilterBarLoader />
      ) : (
        <>
          <StyledBox display="flex" alignItems="center" rightGutter={3}>
            <TextBodyMedium color={theme.colors.text.g3} bold sx={{ marginRight: '12px' }}>
              Group by :
            </TextBodyMedium>
            <div style={{ width: '200px' }}>
              <Select
                value={selectedGroupBy}
                handleChange={(option) => {
                  sendSelectedGroupByEvent({
                    eventProperties: {
                      source: 'Feedback',
                      type: option?.value,
                    },
                  });
                  selectGroupBy(option.apiValue);
                }}
                options={modifiedGroupByKeys}
                name="groupBy"
                placeholder="Group by"
              />
            </div>
            {/* {modifiedGroupByKeys.map((groupBy, index) => (
                <MenuItem key={`groupByKey-option-${index}`} value={groupBy}>
                  <TextBodyMedium color={theme.colors.text.g3} bold>
                    {groupBy.displayName}
                  </TextBodyMedium>
                </MenuItem>
              ))} */}
          </StyledBox>
          {/* <StyledBox display="flex" alignItems="center" rightGutter={3}>
            <TextBodyMedium color={theme.colors.text.g3} bold sx={{ marginRight: '12px' }}>
              Split by :
            </TextBodyMedium>
            <div style={{ width: '200px' }}>
              <Select
                value={selectedSplitBy}
                handleChange={option => {
                  selectSplitByKey(option.apiValue);
                }}
                options={allSplitByKeys}
                name="splitBy"
                placeholder={'Split by'}
              />
            </div>

          </StyledBox> */}
          {/* <SplitButton
            style={{ marginLeft: 'auto' }}
            options={splitButtonOptions}
            indexesToRender={[1]}
          /> */}
          <AddFeedbackButton
            onClickShareFeedback={handleShareNewFeedbackClick}
            onClickRequestFeedback={handleRequestNewFeedbackClick}
          />
          {/* CSV download to be enabled when BE is finished */}
          {/* <DownloadCSVData
            localStorageKey={localStorageKeys.analyticsFeedbackCSVDownloads}
            downloadText="Download Feedback Data"
            generateCSVData={generateCSVData}
          /> */}
          <DownloadCSVDataV1
            downloadFileHandler={generateCSVData}
            localStorageKey={localStorageKeys.reviewTablesCSVDownloads}
            fileName={sideNavSelectedCohort?.type}
          />
        </>
      )}
    </Flex>
  );
};

export default ContinuousFeedbackAnalyticsFilterBar;
